/** Découpe un texte de l'API en paragraphes non vides. */
export const paragraphs = (t = ''): string[] =>
  t.split(/\n+/).map((p) => p.trim()).filter(Boolean);

export const capitalize = (t = ''): string => {
  const s = t.trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
};

/**
 * Résumé court d'un texte: première phrase; si elle dépasse `max`, coupe à la dernière virgule,
 * sinon sur un mot entier en retirant les petits mots orphelins (« le », « de »…).
 */
export const summary = (t = '', max = 120, sentences = 1): string => {
  const first = (paragraphs(t)[0] ?? '').split(/(?<=[.!?])\s/).slice(0, sentences).join(' ').trim();
  const one = first.split(/(?<=[.!?])\s/)[0];
  if (first.length <= max) return first;
  if (sentences > 1 && one.length <= max) return one;
  const comma = first.lastIndexOf(',', max - 3);
  if (comma > max / 2) return `${first.slice(0, comma)}…`;
  const words = first.slice(0, max - 3).split(' ').slice(0, -1);
  while (words.length > 1 && words[words.length - 1].replace(/[,;:]$/, '').length <= 3) words.pop();
  return `${words.join(' ').replace(/[,;:]$/, '')}…`;
};

/** Description d'un projet: ignore les lignes de fiche (« Localisation : … ») de certains sous-titres. */
export const projectSummary = (t = '', max = 220): string => {
  const lines = paragraphs(t).filter((l) => !/^(localisation|type de projet|maître d|client)\s*:/i.test(l));
  return summary(lines.join(' ') || t, max, 2);
};

export const formatYear = (iso?: string | null): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : String(d.getFullYear());
};

/** « MUBIALA IDRIS » → « Mubiala Idris »; les mots déjà bien écrits sont conservés. */
export const properName = (name = ''): string =>
  name.trim().split(/\s+/).map((w) => (w.length > 1 && w === w.toUpperCase() ? w.charAt(0) + w.slice(1).toLowerCase() : w)).join(' ');

/**
 * Fonction d'une personne. Certaines fiches contiennent une phrase (« Nom, CEO & Founder de X, entrepreneur… »):
 * on retire le nom en tête et on garde le premier segment.
 */
export const cleanRole = (name = '', position = '', max = 70): string => {
  const escaped = name.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\s+/g, '\\s+');
  let role = position.trim().replace(new RegExp(`^${escaped}\\s*[,:–-]\\s*`, 'i'), '');
  const firstClause = role.split(/,\s+/)[0];
  if (firstClause.length >= 8) role = firstClause;
  return summary(role.replace(/\.$/, ''), max);
};

/** « kisangani/RDC », « KIKWIT/RDC », « kinshasa, rdc » → « Kisangani, RDC »; les sigles connus restent en majuscules. */
export const formatLocation = (loc = ''): string =>
  loc
    .split(/\s*[,/]\s*/)
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => (/^(rdc|usa|uk|ue)$/i.test(part) ? part.toUpperCase() : part.toLowerCase().replace(/(^|[\s'’-])(\p{L})/gu, (_, a: string, b: string) => a + b.toUpperCase())))
    .join(', ');

/**
 * Sépare un texte en « accroche » (les premières phrases, jusqu'à `max` caractères) et « suite » (le reste en paragraphes).
 * Sert à mettre l'essentiel en grand, puis le détail en texte courant, sans rien couper.
 */
export function splitLead(text = '', max = 340): { lead: string; rest: string[] } {
  const paras = paragraphs(text);
  if (paras.length === 0) return { lead: '', rest: [] };
  const sentences = paras[0].split(/(?<=[.!?])\s+/);
  let lead = sentences[0];
  let used = 1;
  while (used < sentences.length && (lead + ' ' + sentences[used]).length <= max) { lead += ' ' + sentences[used]; used++; }
  const tail = sentences.slice(used).join(' ');
  return { lead, rest: [...(tail ? [tail] : []), ...paras.slice(1)] };
}

const BLOCK_TAGS = new Set(['P', 'DIV', 'LI', 'UL', 'OL', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BLOCKQUOTE', 'TR', 'SECTION', 'ARTICLE']);

/**
 * Texte riche de l'admin (HTML de l'éditeur) → texte brut, un paragraphe par ligne.
 * Les intertitres deviennent « Titre : » (reconnus par les pages de détail), les puces une ligne chacune.
 * Le HTML est analysé par DOMParser: aucun script n'est exécuté et aucune balise n'atteint l'affichage.
 * Un texte sans balise est renvoyé tel quel.
 */
export const htmlToText = (html = ''): string => {
  if (!/<\/?[a-z][^>]*>/i.test(html)) return html;
  if (typeof DOMParser === 'undefined') return html.replace(/<\/(p|div|li|h[1-6])>|<br\s*\/?>/gi, '\n').replace(/<[^>]*>/g, '').trim();

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const lines: string[] = [];
  let cur = '';
  const flush = () => {
    const t = cur.replace(/\s+/g, ' ').trim();
    if (t) lines.push(t);
    cur = '';
  };
  const walk = (n: Node) => {
    if (n.nodeType === Node.TEXT_NODE) {
      cur += n.textContent ?? '';
      return;
    }
    if (n.nodeType !== Node.ELEMENT_NODE) return;
    const el = n as Element;
    const tag = el.tagName;
    if (tag === 'SCRIPT' || tag === 'STYLE') return;
    if (tag === 'BR') {
      flush();
      return;
    }
    const block = BLOCK_TAGS.has(tag);
    if (block) flush();
    el.childNodes.forEach(walk);
    if (block) {
      if (/^H[1-6]$/.test(tag)) {
        const t = cur.trim();
        if (t && !/[:.!?]$/.test(t)) cur = `${t} :`;
      }
      flush();
    }
  };
  doc.body.childNodes.forEach(walk);
  flush();
  return lines.join('\n');
};

const ALLOWED_TAGS = new Set(['P', 'BR', 'STRONG', 'B', 'EM', 'I', 'U', 'UL', 'OL', 'LI', 'H2', 'H3', 'H4', 'BLOCKQUOTE', 'A', 'IMG']);
const DROP_TAGS = new Set(['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'FORM', 'INPUT', 'BUTTON', 'LINK', 'META', 'SVG', 'MATH']);

/**
 * HTML de l'éditeur de l'admin → HTML assaini (liste blanche de balises, aucun attribut sauf href/src/alt).
 * Les liens ne gardent que http(s) et mailto, les images que https. H1 devient H2 (un seul h1 par page).
 */
export const sanitizeHtml = (html = ''): string => {
  if (typeof DOMParser === 'undefined' || !/<\/?[a-z][^>]*>/i.test(html)) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const out = document.createElement('div');
  const clean = (node: Node, into: Node) => {
    node.childNodes.forEach((n) => {
      if (n.nodeType === Node.TEXT_NODE) {
        into.appendChild(document.createTextNode(n.textContent ?? ''));
        return;
      }
      if (n.nodeType !== Node.ELEMENT_NODE) return;
      const el = n as Element;
      let tag = el.tagName;
      if (DROP_TAGS.has(tag)) return;
      if (tag === 'H1') tag = 'H2';
      if (!ALLOWED_TAGS.has(tag)) {
        clean(el, into);
        return;
      }
      const copy = document.createElement(tag.toLowerCase());
      if (tag === 'A') {
        const href = el.getAttribute('href') ?? '';
        if (!/^(https?:|mailto:)/i.test(href)) {
          clean(el, into);
          return;
        }
        copy.setAttribute('href', href);
        copy.setAttribute('rel', 'noopener noreferrer');
        if (/^https?:/i.test(href)) copy.setAttribute('target', '_blank');
      }
      if (tag === 'IMG') {
        const src = el.getAttribute('src') ?? '';
        if (!/^https:/i.test(src)) return;
        copy.setAttribute('src', src);
        copy.setAttribute('alt', el.getAttribute('alt') ?? '');
        copy.setAttribute('loading', 'lazy');
      }
      clean(el, copy);
      into.appendChild(copy);
    });
  };
  clean(doc.body, out);
  return out.innerHTML.replace(/<p>(\s|&nbsp;)*<\/p>/g, '');
};

const RICH_KEYS = new Set(['description', 'content', 'title', 'subtitle']);

/** Convertit en place les champs riches d'une réponse de l'API (objets et listes imbriqués). */
export const normalizeRich = <T,>(data: T): T => {
  const visit = (v: unknown): void => {
    if (Array.isArray(v)) return v.forEach(visit);
    if (v && typeof v === 'object') {
      const o = v as Record<string, unknown>;
      for (const k of Object.keys(o)) {
        const x = o[k];
        if (typeof x === 'string' && RICH_KEYS.has(k)) {
          o[k] = htmlToText(x);
          // Garde aussi la version mise en forme des textes longs, pour les pages de détail
          if ((k === 'description' || k === 'content') && /<\/?[a-z][^>]*>/i.test(x)) {
            const html = sanitizeHtml(x);
            if (html) o[`${k === 'description' ? 'description' : 'content'}Html`] = html;
          }
        }
        else visit(x);
      }
    }
  };
  visit(data);
  return data;
};
